import { useState } from "react";
import axios from 'axios';
import { useHistory } from "react-router-dom";
import { 
    Box, 
    Alert, 
    TextField,
    Button, 
    Paper,
    Typography,
    LinearProgress,
    IconButton,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SaveIcon from '@mui/icons-material/Save';
import ImageIcon from '@mui/icons-material/Image';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TranslateIcon from '@mui/icons-material/Translate';

const AddArticle = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [title, setTitle] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [body, setBody] = useState("");
    const [bodyEn, setBodyEn] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [imageAlt, setImageAlt] = useState("");
    const [imageTitle, setImageTitle] = useState("");
    const [imageCaption, setImageCaption] = useState("");
    const [imageCredit, setImageCredit] = useState("");
    const [imageSource, setImageSource] = useState("");
    const [imageSourceUrl, setImageSourceUrl] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [previewUrl, setPreviewUrl] = useState(null);
    
    let history = useHistory();

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        setSelectedFile(file);
        
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setSelectedFile(null);
        setPreviewUrl(null);
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
       
        const token = localStorage.getItem("token");
        const data = new FormData();

        data.append("title", title);
        if (titleEn) data.append("title_en", titleEn);
        data.append("body", body);
        if (bodyEn) data.append("body_en", bodyEn);
        if (selectedFile) data.append("image", selectedFile);
        if (imageAlt) data.append("image_alt", imageAlt);
        if (imageTitle) data.append("image_title", imageTitle);
        if (imageCaption) data.append("image_caption", imageCaption);
        if (imageCredit) data.append("image_credit", imageCredit);
        if (imageSource) data.append("image_source", imageSource);
        if (imageSourceUrl) data.append("image_source_url", imageSourceUrl);

        axios.post("articles", 
            data,
            {
                headers: {
                    Authorization: "Bearer " + token 
                }
            }
        )
        .then(function (response) {
            console.log(response);
            history.push("/admin-article");
        })
        .catch(function (error) {
            setErrorMessage(error.response?.data?.message || "Une erreur est survenue");
            console.log(error);
        })
        .finally(() => {
            setIsLoading(false);
        });
    };

    return (
        <Box sx={{ width: "90%", margin: "auto", mt: 4, mb: 4 }}>
            {isLoading && <LinearProgress color="success" />}
            
            <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                <Typography 
                    variant="h5" 
                    sx={{ 
                        color: '#00a652',
                        mb: 4,
                        fontWeight: 500,
                        borderBottom: '2px solid #00a652',
                        paddingBottom: 1,
                        display: 'inline-block'
                    }}
                >
                    Ajouter un Article
                </Typography>

                {errorMessage && (
                    <Alert 
                        severity="error"
                        sx={{ mb: 3 }}
                        action={
                            <IconButton
                                color="inherit"
                                size="small"
                                onClick={() => setErrorMessage("")}
                            >
                                <CloseIcon fontSize="inherit" />
                            </IconButton>
                        }
                    >
                        {errorMessage}
                    </Alert>
                )}

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Titre principal */}
                        <TextField
                            label="Titre *"
                            value={title}
                            required
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '&:hover fieldset': { borderColor: '#00a652' },
                                    '&.Mui-focused fieldset': { borderColor: '#00a652' },
                                }
                            }}
                        />

                        {/* Contenu principal */}
                        <Box sx={{ minHeight: '320px' }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.secondary' }}>
                                Contenu *
                            </Typography>
                            <ReactQuill
                                theme="snow"
                                value={body}
                                onChange={setBody}
                                style={{ height: '250px', marginBottom: '20px' }}
                            />
                        </Box>

                        {/* Version anglaise (Accordéon) */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <TranslateIcon color="primary" />
                                    <Typography>Version anglaise (optionnel)</Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <TextField
                                        label="Titre (EN)"
                                        value={titleEn}
                                        onChange={(e) => setTitleEn(e.target.value)}
                                        fullWidth
                                    />
                                    <Box sx={{ minHeight: '320px' }}>
                                        <Typography variant="subtitle1" sx={{ mb: 1, color: 'text.secondary' }}>
                                            Contenu (EN)
                                        </Typography>
                                        <ReactQuill
                                            theme="snow"
                                            value={bodyEn}
                                            onChange={setBodyEn}
                                            style={{ height: '250px' }}
                                        />
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </Accordion>

                        {/* Image principale */}
                        <Box sx={{ 
                            border: '2px dashed #00a652',
                            borderRadius: 1,
                            p: 3,
                            backgroundColor: 'rgba(0,166,82,0.04)',
                            textAlign: 'center'
                        }}>
                            {previewUrl ? (
                                <Box>
                                    <Box sx={{ position: 'relative', display: 'inline-block' }}>
                                        <img 
                                            src={previewUrl} 
                                            alt="Preview" 
                                            style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '8px' }}
                                        />
                                        <IconButton
                                            onClick={removeImage}
                                            sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                bgcolor: 'error.main',
                                                color: 'white',
                                                '&:hover': { bgcolor: 'error.dark' }
                                            }}
                                            size="small"
                                        >
                                            <CloseIcon />
                                        </IconButton>
                                    </Box>
                                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                                        {selectedFile?.name}
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    <input
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        id="image-upload"
                                        type="file"
                                        onChange={handleFileChange}
                                    />
                                    <label htmlFor="image-upload">
                                        <Button
                                            component="span"
                                            startIcon={<ImageIcon />}
                                            sx={{
                                                color: '#00a652',
                                                borderColor: '#00a652',
                                                '&:hover': { backgroundColor: 'rgba(0,166,82,0.08)' }
                                            }}
                                            variant="outlined"
                                        >
                                            Sélectionner une image
                                        </Button>
                                    </label>
                                </>
                            )}
                        </Box>

                        {/* Métadonnées de l'image (Accordéon) */}
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Métadonnées de l'image (optionnel)</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Texte alternatif (Alt)"
                                            value={imageAlt}
                                            onChange={(e) => setImageAlt(e.target.value)}
                                            fullWidth
                                            placeholder="Description pour l'accessibilité"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Titre de l'image"
                                            value={imageTitle}
                                            onChange={(e) => setImageTitle(e.target.value)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            label="Légende"
                                            value={imageCaption}
                                            onChange={(e) => setImageCaption(e.target.value)}
                                            fullWidth
                                            multiline
                                            rows={2}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Crédit photo"
                                            value={imageCredit}
                                            onChange={(e) => setImageCredit(e.target.value)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Source"
                                            value={imageSource}
                                            onChange={(e) => setImageSource(e.target.value)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="URL de la source"
                                            value={imageSourceUrl}
                                            onChange={(e) => setImageSourceUrl(e.target.value)}
                                            fullWidth
                                            type="url"
                                        />
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>

                        {/* Bouton de soumission */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={() => history.push("/admin-article")}
                                disabled={isLoading}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={<SaveIcon />}
                                disabled={isLoading}
                                sx={{
                                    backgroundColor: '#00a652',
                                    '&:hover': { backgroundColor: '#008c46' },
                                    px: 4
                                }}
                            >
                                {isLoading ? "Enregistrement..." : "Enregistrer"}
                            </Button>
                        </Box>
                    </Box>
                </form>
            </Paper>
        </Box>
    );
};

export default AddArticle;